import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, TrendingUp, Megaphone } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './AdvertisingCampaignCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './AdvertisingCampaignCostCalculatorPage/components/ResultsOverview';
import BudgetDistribution from './AdvertisingCampaignCostCalculatorPage/components/BudgetDistribution';
import QuickAdjustmentSidebar from './AdvertisingCampaignCostCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './AdvertisingCampaignCostCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './AdvertisingCampaignCostCalculatorPage/components/TipsSidebar';
import InfoContent from './AdvertisingCampaignCostCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  campaignName: 'Моя рекламная кампания',
  totalBudget: '100000',
  campaignDuration: '30', // дней
  platforms: {
    search: {
      enabled: true,
      budget: '30',
      cpc: '15',
      impressions: '0',
      ctr: '2.5',
      conversionRate: '2'
    },
    social: {
      enabled: true,
      budget: '35',
      cpc: '12',
      impressions: '0',
      ctr: '1.8',
      conversionRate: '1.5'
    },
    display: {
      enabled: true,
      budget: '20',
      cpc: '5',
      impressions: '0',
      ctr: '0.8',
      conversionRate: '0.5'
    },
    video: {
      enabled: false,
      budget: '10',
      cpc: '25',
      impressions: '0',
      ctr: '1.2',
      conversionRate: '1'
    },
    email: {
      enabled: false,
      budget: '5',
      cpc: '3',
      impressions: '0',
      ctr: '3.5',
      conversionRate: '3'
    }
  },
  targetAudience: {
    size: '100000',
    reachPercent: '60'
  },
  goals: {
    awarenessWeight: '30',
    engagementWeight: '30',
    conversionWeight: '40'
  },
  advanced: {
    includeProductionCosts: false,
    productionCost: '20000',
    includeAgencyFee: false,
    agencyFeePercent: '15'
  }
};

// Определение типов рекламных платформ
export const platformTypes = [
  { id: 'search', name: 'Поисковая реклама', iconName: 'Search', defaultCpc: 15 },
  { id: 'social', name: 'Социальные сети', iconName: 'Users', defaultCpc: 12 },
  { id: 'display', name: 'Медийная реклама', iconName: 'Image', defaultCpc: 5 },
  { id: 'video', name: 'Видеореклама', iconName: 'Video', defaultCpc: 25 },
  { id: 'email', name: 'Email-маркетинг', iconName: 'Mail', defaultCpc: 3 }
];

/**
 * AdvertisingCampaignCostCalculatorPage Component - Полный калькулятор стоимости рекламной кампании.
 */
const AdvertisingCampaignCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('advertising-calculator') || {
    id: 'advertising-calculator',
    name: 'Калькулятор стоимости рекламы',
    description: 'Расчет бюджета рекламной кампании, распределение по каналам и прогноз эффективности',
    metaTitle: 'Калькулятор стоимости рекламной кампании | Gensio',
    metaDescription: 'Рассчитайте оптимальный бюджет для вашей рекламной кампании, распределите его по каналам и спрогнозируйте эффективность и окупаемость'
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости рекламной кампании | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте оптимальный бюджет для вашей рекламной кампании, распределите его по каналам и спрогнозируйте эффективность и окупаемость';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCampaigns, setSavedCampaigns] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных кампаний из localStorage ---
  useEffect(() => {
    const savedCampaignsData = localStorage.getItem('savedCampaigns');
    if (savedCampaignsData) {
      try {
        setSavedCampaigns(JSON.parse(savedCampaignsData));
      } catch (e) {
        console.error("Error loading saved campaigns:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'search':
          // Установка только поисковой рекламы
          newState.platforms = {
            ...newState.platforms,
            social: { ...newState.platforms.social, enabled: false },
            display: { ...newState.platforms.display, enabled: false },
            video: { ...newState.platforms.video, enabled: false },
            email: { ...newState.platforms.email, enabled: false }
          };
          break;
        case 'social':
          // Установка только рекламы в соцсетях
          newState.platforms = {
            ...newState.platforms,
            search: { ...newState.platforms.search, enabled: false },
            display: { ...newState.platforms.display, enabled: false },
            video: { ...newState.platforms.video, enabled: false },
            email: { ...newState.platforms.email, enabled: false }
          };
          break;
        case 'comprehensive':
          // Активация всех каналов для комплексной кампании
          newState.platforms = {
            ...newState.platforms,
            search: { ...newState.platforms.search, enabled: true },
            social: { ...newState.platforms.social, enabled: true },
            display: { ...newState.platforms.display, enabled: true },
            video: { ...newState.platforms.video, enabled: true },
            email: { ...newState.platforms.email, enabled: true }
          };
          break;
        case 'low-budget':
          // Установка низкого бюджета
          newState.totalBudget = '30000';
          newState.platforms = {
            ...newState.platforms,
            search: { ...newState.platforms.search, enabled: true },
            social: { ...newState.platforms.social, enabled: true },
            display: { ...newState.platforms.display, enabled: false },
            video: { ...newState.platforms.video, enabled: false },
            email: { ...newState.platforms.email, enabled: false }
          };
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение кампании в localStorage
  const saveCampaign = (name) => {
    if (!results) return;

    const newSavedCampaign = {
      id: Date.now().toString(),
      name: name || formData.campaignName || `Кампания с бюджетом ${formData.totalBudget}₽`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCampaigns = [...savedCampaigns, newSavedCampaign];
    setSavedCampaigns(updatedSavedCampaigns);
    localStorage.setItem('savedCampaigns', JSON.stringify(updatedSavedCampaigns));

    return newSavedCampaign;
  };

  // Удаление сохраненной кампании
  const deleteSavedCampaign = (id) => {
    const updatedSavedCampaigns = savedCampaigns.filter(campaign => campaign.id !== id);
    setSavedCampaigns(updatedSavedCampaigns);
    localStorage.setItem('savedCampaigns', JSON.stringify(updatedSavedCampaigns));
  };

  // Загрузка сохраненной кампании
  const loadSavedCampaign = (id) => {
    const campaignToLoad = savedCampaigns.find(campaign => campaign.id === id);
    if (campaignToLoad) {
      setFormData(campaignToLoad.formData);
      setResults(campaignToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Megaphone className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости рекламной кампании'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {tool?.description || metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              platformTypes={platformTypes}
              saveCampaign={saveCampaign}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <BudgetDistribution 
                  results={results} 
                  platforms={formData.platforms}
                />
              </>
            )}

            {/* --- Информационный контент о рекламе --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения кампаний --- */}
            <ComparisonSidebar 
              savedCampaigns={savedCampaigns}
              deleteSavedCampaign={deleteSavedCampaign}
              loadSavedCampaign={loadSavedCampaign}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default AdvertisingCampaignCostCalculatorPage;